/**
 * `/set-password?token=…&next=/s/{slug}` — where a "Share with people" link
 * lands (Manage → Share with people).
 *
 * The person picks a password; `POST /auth/set-password` sets it and signs
 * them in, and they go on to the page that was shared (`next`, same-origin
 * only), else their list of shared pages. The link works once: the API
 * refuses a used or expired token, and this page says what to do about it.
 */

import { ApiError } from "@chai/api-client";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { type FormEvent, useState } from "react";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useDocumentTitle } from "@/lib/document-title";

import { useApi } from "../../api";

const SearchSchema = z.object({
  token: z.string().optional(),
  next: z.string().optional(),
});

export const Route = createFileRoute("/_auth/set-password")({
  validateSearch: (search) => SearchSchema.parse(search),
  component: SetPasswordPage,
});

/** Same-origin paths only, as sign-in's `returnTo`. */
function safeNext(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

function SetPasswordPage() {
  const api = useApi();
  const { token, next } = useSearch({ from: "/_auth/set-password" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle(["Choose a password"]);

  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || password.length < 8 || password !== confirm) return;
    setError(null);
    setPending(true);
    try {
      await api.request(
        "/auth/set-password",
        { method: "POST", body: JSON.stringify({ token, password }) },
        false,
      );
      // A full load: the session cookie is new, and every query should read it.
      window.location.href = safeNext(next) ?? "/shared?auto=1";
      return;
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(err.message);
      } else if (err instanceof ApiError && err.status === 422) {
        setError("Use at least 8 characters.");
      } else {
        setError("Something went wrong on our end. Please try again in a moment.");
      }
    } finally {
      setPending(false);
    }
  };

  if (!token) {
    return (
      <div className="flex flex-col gap-4 pt-10">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          This link is incomplete
        </h1>
        <p className="text-sm text-muted-foreground">
          Open the link you were sent again, the whole of it. If it still doesn't
          work, ask the person who shared the page with you for a new one.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-10">
      <header>
        <p className="meta-strip mb-2">A page was shared with you</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Choose a password
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You'll use it with your email to open the page again later.
        </p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={submit} noValidate>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="new-password">Password</FieldLabel>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={show ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    aria-label={show ? "Hide password" : "Show password"}
                    aria-pressed={show}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    {show ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
                <FieldDescription>At least 8 characters.</FieldDescription>
              </Field>
              <Field data-invalid={mismatch ? "" : undefined}>
                <FieldLabel htmlFor="confirm-password">Type it again</FieldLabel>
                <Input
                  id="confirm-password"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  aria-invalid={mismatch || undefined}
                />
                {mismatch ? <FieldDescription>The two don't match yet.</FieldDescription> : null}
              </Field>

              {error ? (
                <Alert variant="destructive" role="alert" aria-live="polite">
                  <AlertTitle>That didn't work</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <Button
                type="submit"
                className="w-full"
                disabled={pending || password.length < 8 || password !== confirm}
              >
                {pending && <Spinner data-icon="inline-start" />}
                Set password and open the page
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
